import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { practiceSettingsPath } from "../../../../shared/navigation/practiceSettings";
import { Modal } from "../../../../shared/ui/modal";
import Button from "../../../../shared/ui/button/Button";
import { useStripeConnectStatus } from "../../application/useStripeConnect";
import { usePaymentAccount } from "../../application/usePayments";
import {
    Payment,
    SendFailure,
    formatMoney,
    isRetryable,
} from "../../domain/types";
import { toLocalDateKey } from "../../../../shared/utils/date";

interface Props {
    isOpen: boolean;
    payment: Payment;
    isSubmitting?: boolean;
    /** Why the last attempt failed; the modal stays open so it can be read. */
    failure?: SendFailure | null;
    onClose: () => void;
    onConfirm: (input: { sent_at: string; request_payment_link: boolean }) => void;
}

const today = () => toLocalDateKey();

/**
 * There is no issuer-completeness check here. The invoicing design blocked
 * this button until the therapist had filled in a tax id and address; nothing
 * is issued now, so nothing can be incomplete.
 */
const SendPaymentModal: React.FC<Props> = ({
    isOpen,
    payment,
    isSubmitting,
    failure = null,
    onClose,
    onConfirm,
}) => {
    const { t } = useTranslation();
    const [sentAt, setSentAt] = useState(today());
    const [requestPaymentLink, setRequestPaymentLink] = useState(false);
    const { data: stripe } = useStripeConnectStatus();
    // Whose Stripe the link would be minted on: the centre's for a patient it
    // holds, the therapist's otherwise. Until it answers, the therapist's own
    // status stands in, as it did before centres existed.
    const { data: account } = usePaymentAccount(payment.patient_id);
    const managedByCenter = Boolean(account?.managed_by_center);

    const stripeConnected = account ? account.stripe_connected : Boolean(stripe?.connected);
    const stripeAvailable = stripe?.available !== false;
    const concept = payment.line_items[0]?.description ?? "";

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            ariaLabel={t("payments.sendModal.title")}
            className="w-full max-w-md p-6"
        >
            <h2 className="mb-1 text-lg font-semibold text-content-primary">
                {t("payments.sendModal.title")}
            </h2>
            <p className="mb-5 text-sm text-content-tertiary">
                {t("payments.sendModal.summary", {
                    concept,
                    total: formatMoney(payment.total),
                })}
            </p>

            <div className="mb-4">
                <label
                    htmlFor="sent-at"
                    className="mb-1 block text-sm font-medium text-content-secondary"
                >
                    {t("payments.fields.sentAt")}
                </label>
                <input
                    id="sent-at"
                    type="date"
                    value={sentAt}
                    onChange={(e) => setSentAt(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
                />
            </div>

            {failure && (
                <div
                    role="alert"
                    className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-500/10 dark:text-red-300"
                >
                    <p>{t(`payments.sendErrors.${failure}`)}</p>
                    {/* Nothing here is a dead end: every failure names the one
                        thing that clears it. */}
                    {failure === "stripe_not_connected" && (
                        <Link
                            to={practiceSettingsPath("payments")}
                            className="mt-1 inline-block font-medium underline"
                        >
                            {t("payments.sendErrors.connectAction")}
                        </Link>
                    )}
                    {failure === "center_stripe_not_connected" && (
                        <p className="mt-1">{t("payments.sendErrors.centerConnectAction")}</p>
                    )}
                    {failure === "payment_link_needs_amount" && (
                        <p className="mt-1">{t("payments.sendErrors.amountAction")}</p>
                    )}
                    {isRetryable(failure) && (
                        <p className="mt-1">{t("payments.sendErrors.retryAction")}</p>
                    )}
                </div>
            )}

            {stripeAvailable && (
                <div className="mb-5">
                    <label className="flex items-start gap-3 text-sm text-content-secondary">
                        <input
                            type="checkbox"
                            checked={requestPaymentLink}
                            // Offered only when it can actually work; the copy
                            // below says where to go rather than leaving a
                            // dead control on screen.
                            disabled={!stripeConnected}
                            onChange={(e) => setRequestPaymentLink(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300"
                        />
                        <span>
                            {t("payments.sendModal.paymentLinkToggle")}
                            {!stripeConnected && (
                                <span className="mt-1 block text-xs text-content-tertiary">
                                    {managedByCenter
                                        ? t("payments.sendModal.centerStripeNotConnectedWarning", {
                                              center: account?.center_name ?? "",
                                          })
                                        : t("payments.sendModal.stripeNotConnectedWarning")}
                                </span>
                            )}
                            {stripeConnected && managedByCenter && (
                                <span className="mt-1 block text-xs text-content-tertiary">
                                    {t("payments.sendModal.chargedByCenter", {
                                        center: account?.center_name ?? "",
                                    })}
                                </span>
                            )}
                        </span>
                    </label>
                </div>
            )}

            <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
                    {t("common.cancel")}
                </Button>
                <Button
                    onClick={() =>
                        onConfirm({
                            sent_at: sentAt,
                            request_payment_link: requestPaymentLink && stripeConnected,
                        })
                    }
                    disabled={isSubmitting}
                    loading={isSubmitting}
                >
                    {t("payments.sendModal.confirm")}
                </Button>
            </div>
        </Modal>
    );
};

export default SendPaymentModal;
